import { useCallback, useEffect, useState } from "react";
import { useIsNative } from "@/lib/platform";
import { useProfile } from "@/lib/api";
import { usePushNotifications } from "@/lib/api/useChat";
import type { PushPrimer } from "./usePushPrimerTypes";

/**
 * Whether this run of the app has already put the OS sheet in front of the
 * user. Module scope on purpose: it has to outlive every component that asks,
 * because the whole point is that several of them do.
 *
 * Not persisted. A user who swiped the sheet away without choosing is still
 * `default` on the next launch, and asking them again then is the reliability
 * this is for. Within one session, asking twice is just a bug the user sees.
 */
let askedThisSession = false;

/** Test seam — nothing in the app should need this. */
export function resetPushAskForTests() {
  askedThisSession = false;
  try {
    localStorage.removeItem(SNOOZE_KEY);
  } catch {
    /* nothing stored, nothing to clear */
  }
}

/**
 * The single place that decides whether to raise the notification prompt.
 *
 * Three screens used to own a copy of this effect, each with its own delay and
 * its own idea of when to fire. Now they call this, and it holds the one rule
 * that matters on iOS: the system sheet can be shown exactly once per install.
 * Spend it twice and the second call is a silent no-op; spend it at the wrong
 * moment and there is no second chance. So this checks the status first and
 * burns the session flag whatever the answer.
 */
export function useAskForPushPermission() {
  const { requestPermission, getPermissionStatus, isSupported } =
    usePushNotifications();

  return useCallback(async () => {
    if (!isSupported || askedThisSession) return;
    askedThisSession = true;
    // `default` is the only state where asking does anything. Granted needs
    // nothing; denied can only be undone in system settings, and calling
    // through anyway would burn a prompt that never appears.
    if ((await getPermissionStatus()) !== "default") return;
    await requestPermission();
  }, [isSupported, getPermissionStatus, requestPermission]);
}

/**
 * Let the app settle before the primer lands. A dialog that arrives over a
 * half-painted screen reads as the app malfunctioning.
 */
const SETTLE_MS = 2000;

/**
 * How long "Not now" holds. The primer is ours, not the OS's, so re-offering
 * costs nothing but goodwill — but a modal on every single app open spends
 * that goodwill fast. Three days puts it back in front of somebody with a
 * dinner still ahead of them without becoming furniture.
 */
const SNOOZE_MS = 3 * 24 * 60 * 60 * 1000;
const SNOOZE_KEY = "push_primer_snoozed_at";

/**
 * Force the primer open, for looking at it.
 *
 * Every condition the real gate checks is one you cannot reproduce at a
 * desktop browser: it wants the native shell, a live booking, and an OS
 * permission still sitting at `default`. So the screen was unreviewable
 * without a build on a device, which is how design regressions get shipped.
 *
 * `?pushprimer=1` shows it anywhere. It only ever forces the dialog to render
 * — accepting still calls straight through to the real platform, which on the
 * web is the browser's own permission prompt, so nothing here can hand
 * somebody a permission they did not grant. Matches the existing `?vpdebug=1`
 * convention (see components/ux/ViewportDebug).
 */
function isForced(): boolean {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).has("pushprimer");
}

function isSnoozed(): boolean {
  try {
    const at = Number(localStorage.getItem(SNOOZE_KEY));
    return Number.isFinite(at) && at > 0 && Date.now() - at < SNOOZE_MS;
  } catch {
    // Private mode, or storage disabled. Treat it as "never snoozed": showing
    // the primer to somebody who declined is a worse failure than not showing
    // it, but not by as much as never asking anybody on a locked-down browser.
    return false;
  }
}

function snooze() {
  try {
    localStorage.setItem(SNOOZE_KEY, String(Date.now()));
  } catch {
    // Nothing to do. The session flag still stops a second primer this run.
  }
}

/**
 * The soft ask that goes in front of the system one, for booked users on the
 * native app.
 *
 * Everything else about push permission can be retried. This cannot: on iOS
 * the system sheet is shown once per install, and a decline is permanent from
 * the app's side — the user has to go to Settings to undo it. So the cold path
 * (open the app, land on the dashboard, get a sheet with no context) is the
 * one that can lose somebody's push forever, and it is the one that gets a
 * primer. Screens where the ask already has context — the card you see the
 * moment you have booked, the chat you just opened — keep asking directly.
 *
 * The primer's own decline is cheap by comparison: it costs a snooze, and the
 * OS prompt is still unspent, which is the entire point of putting it first.
 */
export type { PushPrimer };

export function usePushPrimer(): PushPrimer {
  const isNative = useIsNative();
  const { data } = useProfile();
  const { getPermissionStatus, isSupported } = usePushNotifications();
  const ask = useAskForPushPermission();
  const [show, setShow] = useState(false);

  const hasBooking = data?.data?.hasBooking ?? false;

  useEffect(() => {
    // `hasBooking` absent means the profile has not loaded (or the server is
    // older than the field), not that there is no booking — so this waits
    // rather than deciding on a missing value.
    const forced = isForced();
    if (!forced) {
      if (!isNative || !hasBooking || !isSupported) return;
      if (askedThisSession || isSnoozed()) return;
    }

    let live = true;
    const tid = setTimeout(async () => {
      // Only `default` is worth a primer. Granted needs nothing, and denied
      // cannot be undone from in here, so a primer would be selling something
      // its own button cannot deliver.
      if (!forced && (await getPermissionStatus()) !== "default") return;
      if (live) setShow(true);
    }, forced ? 0 : SETTLE_MS);

    return () => {
      live = false;
      clearTimeout(tid);
    };
  }, [isNative, hasBooking, isSupported, getPermissionStatus]);

  const allow = useCallback(async () => {
    setShow(false);
    await ask();
  }, [ask]);

  const dismiss = useCallback(() => {
    setShow(false);
    // A forced preview must not leave a real snooze behind, or looking at the
    // screen once quietly suppresses it for the next three days.
    if (isForced()) return;
    snooze();
    // Burn the session flag too, so nothing else raises the sheet on this run
    // straight after they have just said "not now" to the polite version.
    askedThisSession = true;
  }, []);

  return { show, allow, dismiss };
}
